import { cn } from '@/lib/utils/cn';

/**
 * Button.
 *
 * The primary action is **black** — a near-black neutral fill with white text,
 * a hairline contact shadow and an inset top highlight. This is the Clerk
 * register and it is deliberate: in a console, colour should mean something.
 * When the primary button is brand purple, purple stops reading as "this is
 * InversePay" and starts reading as "this is a button", and every genuinely
 * brand-coloured surface next to it loses its voice. Black is neutral, so the
 * one purple thing on screen keeps its meaning.
 *
 * Brand purple is still available as `variant="brand"` for the handful of
 * places the brand *is* the message — the upgrade CTA, the auth panel.
 *
 * Note the hover direction on black: `ink-soft` is lighter than `ink`. There
 * is no useful "darker" at #131316, and a fill that tries to go blacker on
 * hover just looks like it stopped responding.
 *
 * No JS here on purpose. Press feedback is a CSS transform, which means this
 * stays a server component and can be rendered from anywhere without dragging
 * a client boundary — and `prefers-reduced-motion` in globals.css already
 * neutralises it.
 */

const VARIANTS = {
  primary:
    'bg-ink text-white shadow-button hover:bg-ink-soft active:bg-ink-strong disabled:hover:bg-ink',
  brand:
    'bg-brand text-white shadow-button hover:bg-brand-strong active:bg-brand-strong disabled:hover:bg-brand',
  /**
   * The frame's one big purple call to action — `#9b51e0`, which is
   * `--color-brand-accent` exactly (sampled from the exported frame at
   * `#9b52e1`). It is the *accent*, not `brand`, and the difference is
   * visible, so it gets its own variant rather than being approximated.
   *
   * White on accent is 4.52:1 — over AA 4.5, but only just, which is why
   * hover darkens to `brand` (9.45:1) rather than lightening and why this
   * variant is for a single page-level action, never for a row of buttons.
   */
  accent:
    'bg-brand-accent text-white shadow-button hover:bg-brand active:bg-brand-strong disabled:hover:bg-brand-accent',
  secondary:
    'bg-surface text-ink border border-line-strong shadow-xs hover:bg-canvas-alt hover:border-muted-soft active:bg-canvas-sunken disabled:hover:bg-surface disabled:hover:border-line-strong',
  soft: 'bg-brand-soft text-brand hover:bg-brand-line active:bg-brand-line disabled:hover:bg-brand-soft',
  ghost:
    'bg-transparent text-muted hover:bg-canvas-alt hover:text-ink active:bg-line-soft disabled:hover:bg-transparent',
  danger: 'bg-danger text-white shadow-button hover:brightness-95 active:brightness-90',
  'danger-soft': 'bg-danger-soft text-danger hover:brightness-95 active:brightness-90',
};

/**
 * `ink` predates the black-primary decision and is now the same thing.
 * Aliased rather than removed so existing call sites keep working.
 */
VARIANTS.ink = VARIANTS.primary;

/**
 * Heights come down a step and every corner is 8px.
 *
 * The radius is the tell. Controls at 8px, cards at 12px, overlays at 16px
 * gives a hierarchy you can read without looking: the smaller and more
 * interactive a thing is, the crisper its corner. At a uniform 10px everywhere
 * a button and the panel behind it belonged to the same layer.
 */
const SIZES = {
  xs: 'h-7 px-2.5 text-xs gap-1.5 rounded-md',
  sm: 'h-8 px-3 text-xs gap-1.5 rounded-md',
  md: 'h-9 px-3.5 text-sm gap-2 rounded-md',
  lg: 'h-10 px-4 text-sm gap-2 rounded-md',
  xl: 'h-11 px-5 text-sm gap-2 rounded-md',
};

/**
 * Square variants for icon-only buttons. A separate map rather than
 * `SIZES[size] + 'px-0'`: `cn` is plain clsx with no tailwind-merge, so
 * `px-0` and `px-4` would both be emitted and stylesheet order — not the
 * call site — would decide the winner.
 */
const ICON_SIZES = {
  xs: 'size-7 rounded-md',
  sm: 'size-8 rounded-md',
  md: 'size-9 rounded-md',
  lg: 'size-10 rounded-md',
  xl: 'size-11 rounded-md',
};

export default function Button({
  as: Tag = 'button',
  variant = 'primary',
  size = 'md',
  type = 'button',
  iconOnly = false,
  fullWidth = false,
  loading = false,
  disabled = false,
  leadingIcon: LeadingIcon,
  trailingIcon: TrailingIcon,
  className,
  children,
  ...props
}) {
  const isDisabled = disabled || loading;
  // `as={Link}` renders a navigation that looks like a button (the dashboard is
  // full of them). `type` and `disabled` are button-only attributes, so they're
  // dropped rather than leaked onto an anchor as invalid DOM props.
  const isButton = Tag === 'button';

  return (
    <Tag
      type={isButton ? type : undefined}
      disabled={isButton ? isDisabled : undefined}
      aria-disabled={!isButton && isDisabled ? true : undefined}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex shrink-0 items-center justify-center font-medium whitespace-nowrap',
        'transition-[background-color,border-color,color,box-shadow,transform,filter]',
        'duration-[--duration-fast] ease-[--ease-out-quart]',
        // Press: a hair of scale rather than a 1px drop. Scale reads as the
        // control taking the force; translate reads as the layout twitching,
        // which is what it looked like in a toolbar of six of them.
        'active:scale-[0.975]',
        'disabled:pointer-events-none disabled:opacity-40 disabled:shadow-none',
        iconOnly ? ICON_SIZES[size] || ICON_SIZES.md : SIZES[size] || SIZES.md,
        VARIANTS[variant] || VARIANTS.primary,
        fullWidth && 'w-full',
        className
      )}
      {...props}
    >
      {loading && (
        <span
          className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-80"
          aria-hidden="true"
        />
      )}
      {!loading && LeadingIcon && <LeadingIcon className="size-4 shrink-0" aria-hidden="true" />}
      {children}
      {TrailingIcon && <TrailingIcon className="size-4 shrink-0" aria-hidden="true" />}
    </Tag>
  );
}

/**
 * Square icon-only button. Previously every call site hand-rolled this with
 * `className="px-2"` on a normal Button, which produced a slightly different
 * hit target each time — and, without tailwind-merge, an unpredictable one.
 *
 * `label` is required: an icon with no accessible name is an unlabelled
 * control, and `title` alone does not reliably reach a screen reader.
 */
export function IconButton({ icon: Icon, label, size = 'md', variant = 'ghost', className, ...props }) {
  return (
    <Button variant={variant} size={size} iconOnly aria-label={label} title={label} className={className} {...props}>
      <Icon className="size-4" aria-hidden="true" />
    </Button>
  );
}
